import $ from 'jquery';
import Vue from "vue";
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component, Emit, Prop } from "vue-property-decorator";
import "vue-select/dist/vue-select.css";
import Datepicker from 'vuejs-datepicker';
import { de, en } from 'vuejs-datepicker/dist/locale';
import { formatDateTime, WindowType } from '../../../../util';
import { BoardEvents, BoardLogItemDto, CardEvents, CardLogItemDto, TicketEvents, TicketLogItemDto, UserNameDto } from "../../../backend/backendapi";
import { KanbanState, SelectedItemDto } from '../../kanban.model';
import { LogEvent, LogItemVM } from "./activityLogs.models";

@Component({
    name: 'ActivityLogComponent',
    components: {
        Datepicker,
        VueGoodTable
    }
})
export default class ActivityLogComponent extends Vue {
    @Prop({ required: true })
    modalId!: string;
    @Prop({ required: true })
    state!: KanbanState;

    //config
    private get datepickerLocale(): any { return this.$i18n.locale === 'de' ? de : en; }

    //config
    private readonly columnsConfig = [
        {
            label: 'Name',
            field: 'info',
            html: true,
        },
        {
            label: 'Datum',
            type: 'date',
            field: (x: LogItemVM) => x.logItem.lastModified,
            formatFn: formatDateTime
        },
        {
            label: 'Username',
            field: (x: LogItemVM) => x.logItem.userId,
            formatFn: this.getUserById,
        }
    ];

    openModal(): void {
        $('#' + this.modalId).modal('show');
    }

    closeModal(): void {
        $('#' + this.modalId).modal('hide');
    }

    private reload(): void {
        this.state.reloadHistory(this.$toasted, this.$i18n);
    }

    //getter
    private get logItems(): LogItemVM[] {
        if (!this.state.history) {
            return [];
        }

        let l: LogItemVM[] = [];
        l.push(...this.state.history.boardEvents.map(this.createLogItemVmForBoardEvent));
        l.push(...this.state.history.ticketEvents.map(this.createLogItemVmForTicketEvent));
        l.push(...this.state.history.cardEvents.map(this.createLogItemVmForCardEvent));
        l.forEach(this.escapeTags)
        l.forEach(this.highlightEmails)
        return l;
    }

    private get users(): UserNameDto[] {
        return this.state.history?.users ?? [];
    }

    private get sortedEvents(): LogItemVM[] {
        return this.logItems.sort((a, b) => b.logItem.lastModified.getTime() - a.logItem.lastModified.getTime());
    }

    //event
    private onRowClick(params: { row: LogItemVM, pageIndex: number; selected, event }): void {
        var logItemVM = params.row;
        var dto: SelectedItemDto = new SelectedItemDto();
        switch (logItemVM.logEvent) {
            case LogEvent.BOARD:
                if (logItemVM.logItem.category === BoardEvents.DELETED)
                    return;
                dto.boardId = (logItemVM.customEvent as BoardLogItemDto).boardId;
                break;
            case LogEvent.TICKET:
                if (logItemVM.logItem.category === TicketEvents.DELETED)
                    return;
                dto.flowId = (logItemVM.customEvent as TicketLogItemDto).ticketId;
                dto.boardId = this.state.boards.find(b => b.tickets?.find(t => t.id == dto.flowId))?.id ?? null;
                break;
            case LogEvent.CARD:
                if (logItemVM.logItem.category === CardEvents.DELETED)
                    return;
                dto.cardId = (logItemVM.customEvent as CardLogItemDto).cardId;
                dto.flowId = this.state.boards.map(b => b.tickets ?? []).reduce((l, r) => l.concat(r), []).find(f => f.cards?.find(c => c.id == dto.cardId))?.id ?? null;
                dto.boardId = this.state.boards.find(b => b.tickets?.find(f => f.id == dto.flowId))?.id ?? null;
                break;
        }

        this.closeModal();
        setTimeout(() => this.state.selectedViaLog = dto, 500); //this is bad, but we have to wait for bootstrap to fully close the modal, otherwise scrolling would not work
    }

    //util
    private getUserById(id: string): string {
        var foundUser = this.users.find(u => u.id == id);
        if (!foundUser)
            return "";
        return foundUser.name;
    }

    private createLogItemVmForBoardEvent(item: BoardLogItemDto): LogItemVM {
        let info = '';
        switch (item.boardEvent) {
            case BoardEvents.CREATED:
                info += this.$t('board').toString() + ' ' + item.logItem.content + ' ' + this.$t('created').toString();
                break;
            case BoardEvents.UPDATED:
                info += this.$t('board').toString() + ' ' + item.logItem.content + ' ' + this.$t('updated').toString();
                break;
            case BoardEvents.DELETED:
                info += this.$t('board').toString() + ' ' + item.logItem.content + ' ' + this.$t('deleted').toString();
                break;
            case BoardEvents.TICKETS_ORDER_UPDATED:
                info += this.$t('board').toString() + ' ' + item.logItem.content + ' '
                    + this.$t('order').toString() + '(' + this.$t('ticket').toString() + ') ' + this.$t('updated').toString();
                break;
            case BoardEvents.APPROVAL_CREATED:
                info += this.$t('kanban.history.board.approvalRequested', [item.logItem.content]).toString();
                break;
            case BoardEvents.APPROVAL_APPROVED:
                info += this.$t('kanban.history.board.approvalApproved', [item.logItem.content]).toString();
                break;
            case BoardEvents.OWNER_CHANGED:
                info += this.$t('kanban.history.board.ownerChanged', [item.logItem.content]).toString();
                break;
        }
        return new LogItemVM(item.logItem, LogEvent.BOARD, info, item);
    }

    private createLogItemVmForTicketEvent(item: TicketLogItemDto): LogItemVM {
        var info = this.$t('ticket').toString() + " " + item.logItem.content + " ";
        switch (item.ticketEvent) {
            case TicketEvents.CREATED:
                info += this.$t('created').toString();
                break;
            case TicketEvents.UPDATED:
                info += this.$t('updated').toString();
                break;
            case TicketEvents.DELETED:
                info += this.$t('deleted').toString();
                break;
            case TicketEvents.CARDS_ORDER_UPDATED:
                info += this.$t('order').toString() + "(" + this.$t('card').toString() + ") " + this.$t('updated').toString();
                break;
        }

        return new LogItemVM(item.logItem, LogEvent.TICKET, info, item);
    }

    private createLogItemVmForCardEvent(item: CardLogItemDto): LogItemVM {
        var info = this.$t('card').toString() + " " + item.logItem.content + " ";
        switch (item.cardEvent) {
            case CardEvents.CREATED:
                info += this.$t('created').toString();
                break;
            case CardEvents.UPDATED:
                info += this.$t('updated').toString();
                break;
            case CardEvents.DELETED:
                info += this.$t('deleted').toString();
                break;
            case CardEvents.READ_CONFIRMATION:
                info = this.$t('kanban.history.card.read', {card: item.logItem.content}).toString();
                break;
        }

        return new LogItemVM(item.logItem, LogEvent.CARD, info, item);
    }

    private escapeTags(item: LogItemVM): void {
        item.info = item.info.replace("<", "&lt;").replace(">", "&gt;");
    }

    private highlightEmails(item: LogItemVM): void {
        item.info = item.info.replace(/(@[^ ]+)/, "<b>$1</b>");
    }

    @Emit('close')
    private close(): void { }
}